// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
// @todo: Migrate to 'Link' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Link} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Modal} from 'semantic-ui-react';

import {useViewDraftsQuery} from '../../generated/graphql';
import {useApolloClient} from '../../state/hooks';
import {useViewer} from '../../state/viewer/hooks';
import * as ViewApi from '../../state/views/api';
import {ViewDraft} from '../../state/views/graphql';
import {isOnGalleryDiscussionView} from '../../util/urls';
import * as Urls from '../../util/urls';

const DEFAULT_MAIN_TEXT =
  'You have an existing draft edit of this report. Would you like to continue working on it?';

const DEFAULT_DISCARD_TEXT = 'Discard and start fresh';

export interface DraftWarningModalProps {
  parentViewID: string;
  mainText?: string;
  discardText?: string;
  flipButtonColors?: boolean;
  renderResumeButton?(
    draftID: string,
    resuming: boolean,
    discarding: boolean,
    setResuming: (r: boolean) => void
  ): ReactNode;
  onNoDraftFound(): void;
  onDiscard(): void;
  onClose(): void;
}

// Note: we should start moving in favor of DraftReportWarningModal whenever possible
const DraftWarningModal: React.FC<DraftWarningModalProps> = React.memo(
  ({
    parentViewID,
    mainText,
    discardText,
    renderResumeButton,
    flipButtonColors = false,
    onNoDraftFound,
    onClose,
    onDiscard,
  }) => {
    const client = useApolloClient();
    const viewer = useViewer();
    const {loading, data} = useViewDraftsQuery({
      variables: {viewID: parentViewID},
      fetchPolicy: 'network-only',
    });

    const draft = useMemo(() => {
      if (data == null || viewer == null) {
        return null;
      }
      return (data.view?.children?.edges.find(
        e => e.node?.user?.id === viewer.id
      )?.node ?? null) as ViewDraft | null;
    }, [data, viewer]);

    useEffect(() => {
      if (loading) {
        return;
      }
      if (draft == null) {
        onNoDraftFound();
      }
      // eslint-disable-next-line
    }, [loading]);

    const [resuming, setResuming] = useState(false);
    const [discarding, setDiscarding] = useState(false);

    const onResume = useCallback(() => setResuming(true), []);

    const onDiscardDraft = useCallback(async () => {
      if (draft == null) {
        return;
      }
      setDiscarding(true);
      await ViewApi.deleteView(client, draft.id);
      onDiscard();
    }, [client, draft, onDiscard]);

    if (draft == null) {
      return null;
    }

    return (
      <Modal size="mini" open onClose={onClose}>
        <Modal.Content>
          <p>{mainText || DEFAULT_MAIN_TEXT}</p>
          <Button
            variant={flipButtonColors ? 'primary' : 'ghost'}
            size="large"
            style={{marginRight: '8px'}}
            disabled={resuming || discarding}
            onClick={onDiscardDraft}>
            {discardText || DEFAULT_DISCARD_TEXT}
          </Button>
          {renderResumeButton?.(
            draft.id,
            resuming,
            discarding,
            setResuming
          ) ?? (
            <Link
              style={{display: 'contents'}}
              to={
                draft.project != null
                  ? Urls.reportEdit(
                      {
                        entityName: draft.project.entityName,
                        projectName: draft.project.name,
                        reportID: parentViewID,
                        reportName: draft.displayName,
                      },
                      `?draftId=${draft.id}`
                    )
                  : (isOnGalleryDiscussionView()
                      ? Urls.galleryDiscussionEdit
                      : Urls.galleryPostEdit)({
                      entityName: draft.entityName,
                      reportID: draft.id,
                      reportName: draft.displayName,
                    })
              }>
              <Button
                variant={!flipButtonColors ? 'primary' : 'ghost'}
                size="large"
                disabled={resuming || discarding}
                onClick={onResume}>
                Resume
              </Button>
              <span />
            </Link>
          )}
        </Modal.Content>
      </Modal>
    );
  }
);

export default DraftWarningModal;
